import React, { Component } from "react";

/*
 * A section-scoped error boundary for the Campaign Optimizer.
 *
 * WHY THIS EXISTS, precisely.
 *
 * /optimus is one route made of six independent panels, each fed by a
 * different backend contract. Before this, a throw anywhere inside any one of
 * them unmounted the WHOLE route and replaced it with the global "Page
 * Unavailable" screen — the operator lost the campaign selector, the platform
 * tabs, the KPI overview and the grade along with whichever panel actually
 * broke, and could not even switch campaigns to get away from the bad data.
 *
 * WHAT THIS IS NOT.
 *
 * It is not a way to make contract violations quiet. It always reports — in
 * production too — and it never substitutes a plausible-looking default for
 * data the server did not send. The panel says, in the product's own voice,
 * that it could not render and that the failure is real. A missing number
 * still reads as missing; a broken panel still reads as broken.
 *
 * The blast radius is the only thing that changes: one panel instead of the
 * entire route.
 */

const IS_PRODUCTION = process.env.NODE_ENV === "production";

const styles = {
  shell: {
    borderRadius: "22px",
    border: "1px solid rgba(194, 77, 231, 0.34)",
    background:
      "radial-gradient(circle at top left, rgba(194, 77, 231, 0.12), transparent 46%), linear-gradient(180deg, rgba(10, 12, 22, 0.92), rgba(0, 0, 0, 0.9))",
    boxShadow: "0 18px 48px rgba(0, 0, 0, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.04)",
    padding: "22px 24px",
    color: "#fff",
    boxSizing: "border-box",
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    padding: "7px 16px",
    borderRadius: "999px",
    border: "1px solid rgba(194, 77, 231, 0.75)",
    background: "radial-gradient(circle at top left, rgba(194, 77, 231, 0.24), rgba(0, 0, 0, 0.9))",
    boxShadow: "0 0 14px rgba(194, 77, 231, 0.45)",
  },
  pillLabel: {
    fontFamily: '"FuturisticArmor", sans-serif',
    fontSize: "0.82rem",
    letterSpacing: "0.16em",
    textTransform: "uppercase",
  },
  title: {
    margin: "14px 0 0",
    fontFamily: '"FuturisticArmor", sans-serif',
    fontSize: "1.02rem",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#fff",
  },
  copy: {
    margin: "10px 0 0",
    maxWidth: "620px",
    fontFamily: '"AquireLight", sans-serif',
    fontSize: "0.9rem",
    lineHeight: 1.65,
    color: "rgba(255, 255, 255, 0.78)",
  },
  button: {
    marginTop: "16px",
    padding: "10px 18px",
    borderRadius: "999px",
    border: "1px solid rgba(136, 156, 231, 0.34)",
    background: "linear-gradient(180deg, rgba(12, 16, 30, 0.92), rgba(0, 0, 0, 0.94))",
    color: "#fff",
    fontFamily: '"FuturisticArmor", sans-serif',
    fontSize: "0.82rem",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    cursor: "pointer",
  },
  debug: {
    marginTop: "16px",
    padding: "12px 14px",
    borderRadius: "14px",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    background: "rgba(255, 255, 255, 0.04)",
    overflowX: "auto",
  },
  pre: {
    margin: 0,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
    fontSize: "0.78rem",
    lineHeight: 1.55,
    color: "rgba(255, 255, 255, 0.86)",
  },
};

class OptimusSectionBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    this.setState({ componentStack: info?.componentStack || "" });

    /* Always reported, production included — see the note at the top. */
    try {
      console.error("[cphere] Optimus section boundary caught an error", {
        section: this.props.name || "(unnamed section)",
        message: error?.message || String(error || "Unknown error"),
        name: error?.name || null,
        stack: error?.stack || null,
        componentStack: info?.componentStack || "",
      });
    } catch (_reportingError) {
      /* Reporting must never itself take the boundary down. */
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, componentStack: "" });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const sectionName = this.props.name || "This section";
    const errorMessage =
      this.state.error?.message || String(this.state.error || "Unknown error");

    return (
      <section className="optx-panel optx-section-boundary" data-optx-section-error={this.props.name || ""}>
        <div style={styles.shell}>
          <div style={styles.pill}>
            <span style={styles.pillLabel}>Section Unavailable</span>
          </div>

          <h2 style={styles.title}>{sectionName}</h2>
          <p style={styles.copy}>
            This section could not be rendered, so Optimus is not showing it rather than
            showing something it cannot stand behind. The rest of the page — campaign
            selection, platform scope and every other panel — is unaffected and still live.
          </p>

          <button type="button" style={styles.button} onClick={this.handleRetry}>
            Retry Section
          </button>

          {!IS_PRODUCTION ? (
            <div style={styles.debug}>
              <pre style={styles.pre}>{errorMessage}</pre>
              {this.state.componentStack ? (
                <pre style={styles.pre}>{this.state.componentStack}</pre>
              ) : null}
            </div>
          ) : null}
        </div>
      </section>
    );
  }
}

export default OptimusSectionBoundary;
